import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

const PRESETS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];

/** Normalize typed method token (uppercase letters, digits, hyphen for rare verbs). */
function normalizeMethodInput(raw) {
  return raw.toUpperCase().replace(/[^A-Z0-9-]/g, '');
}

export default function HttpMethodPicker({ value, onChange, getMethodColor }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 172 });
  const triggerRef = useRef(null);
  const inputRef = useRef(null);

  const colorOf = (m) => (typeof getMethodColor === 'function' ? getMethodColor(m) : '#6b7280');

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const measure = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setPos({
      top: r.bottom + 6,
      left: r.left,
      width: Math.max(r.width, 176),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    measure();
  }, [open, value, measure]);

  useEffect(() => {
    if (!open) return;
    const onResizeScroll = () => measure();
    window.addEventListener('resize', onResizeScroll);
    window.addEventListener('scroll', onResizeScroll, true);
    return () => {
      window.removeEventListener('resize', onResizeScroll);
      window.removeEventListener('scroll', onResizeScroll, true);
    };
  }, [open, measure]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  const pickPreset = (m) => {
    onChange(m);
    close();
  };

  const toggle = () => {
    setOpen((o) => {
      const next = !o;
      if (next) {
        requestAnimationFrame(() => inputRef.current?.blur());
      }
      return next;
    });
  };

  const vv = (value || 'GET').toUpperCase();

  const handleInputChange = (e) => {
    const next = normalizeMethodInput(e.target.value);
    onChange(next || 'GET');
  };

  const handleInputBlur = (e) => {
    const t = normalizeMethodInput(e.target.value);
    if (!t) onChange('GET');
    else onChange(t);
  };

  const dropdown =
    typeof document !== 'undefined' && document.body
      ? createPortal(
          <>
            <div
              className="method-picker-backdrop"
              aria-hidden
              onMouseDown={(e) => {
                e.preventDefault();
                close();
              }}
            />
            <div
              className="method-picker-dropdown"
              role="listbox"
              style={{
                position: 'fixed',
                top: pos.top,
                left: pos.left,
                minWidth: pos.width,
                zIndex: 10040,
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="method-picker-list">
                {PRESETS.map((m) => (
                  <button
                    key={m}
                    type="button"
                    role="option"
                    aria-selected={vv === m}
                    className={`method-picker-item ${vv === m ? 'method-picker-item--active' : ''}`}
                    onClick={() => pickPreset(m)}
                  >
                    <span className="method-picker-item-label" style={{ color: colorOf(m) }}>
                      {m}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </>,
          document.body
        )
      : null;

  return (
    <>
      <div
        ref={triggerRef}
        className="request-url-bar__method-trigger"
      >
        <input
          ref={inputRef}
          type="text"
          value={vv}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          spellCheck={false}
          autoComplete="off"
          aria-label="HTTP method"
          className="request-url-bar__method-trigger-input"
          style={{ color: colorOf(vv) }}
        />
        <button
          type="button"
          className="request-url-bar__method-trigger-chevron-btn"
          onMouseDown={(e) => {
            e.preventDefault();
          }}
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-label="Choose preset HTTP method"
          tabIndex={-1}
        >
          <ChevronDown
            size={11}
            strokeWidth={2.5}
            className={`method-picker-trigger-chevron text-gray-400 ${open ? 'method-picker-trigger-chevron--open' : ''}`}
            aria-hidden
          />
        </button>
      </div>
      {open ? dropdown : null}
    </>
  );
}
